import { Injectable, inject } from '@angular/core';
import { TurnCorrelation } from '../transports/ai-transport';
import { AiTelemetryService } from './ai-telemetry.service';

/**
 * Submits a rep's thumbs vote on a Nova AI card as Foundry's out-of-the-box feedback event, written
 * through {@link AiTelemetryService} to the App Insights resource registered on the Foundry project
 * that served the turn.
 *
 * A vote attaches to the agent's trace as a Foundry annotation only when it carries the trace's
 * `operation_Id` (`gen_ai.response.id` alone just enables a KQL join). The transport learns that id
 * by sending its own `traceparent` on the agent call and records it here via {@link rememberTurn};
 * this service stamps it onto the event.
 */
@Injectable({ providedIn: 'root' })
export class AssistantFeedbackService {
  readonly #telemetry = inject(AiTelemetryService);
  /** responseId → the turn's trace correlation, recorded by the transport as each turn completes. */
  readonly #correlations = new Map<string, TurnCorrelation>();

  /**
   * Records a completed turn's trace correlation (called by the transport). Bounded so a long
   * session can't grow it unbounded — only recent turns are votable, and older cards drop the control.
   */
  rememberTurn(correlation: TurnCorrelation): void {
    if (!correlation.responseId || !correlation.traceId) return;
    this.#correlations.set(correlation.responseId, correlation);
    if (this.#correlations.size > 200) {
      this.#correlations.delete(this.#correlations.keys().next().value as string);
    }
  }

  /**
   * Records a vote for the turn identified by `responseId` (the Responses API `response.id` the
   * card was rendered with). No-ops when `responseId` is empty — a card the rep can't correlate
   * back to a turn (e.g. restored history) never shows the feedback control in the first place,
   * but this is the same guard belt-and-suspenders.
   */
  submitFeedback(responseId: string, up: boolean, comment?: string): void {
    if (!responseId) return;
    const correlation = this.#correlations.get(responseId);

    const params: Record<string, string> = {
      'gen_ai.evaluation.name': 'task_completion',
      // String, not a number: the App Insights web SDK routes numeric props to customMeasurements,
      // but Foundry reads the score from customDimensions — a numeric value silently drops out and
      // the annotation never renders.
      'gen_ai.evaluation.score.value': up ? '1.0' : '0.0',
      'gen_ai.evaluation.score.label': up ? 'pass' : 'fail',
      'gen_ai.evaluation.explanation': comment ?? '',
      'gen_ai.response.id': responseId,
      'microsoft.gen_ai.human_evaluation.source': 'end_user',
      'microsoft.gen_ai.evaluation.actor.type': 'human',
    };
    if (correlation) {
      if (correlation.agentId) params['gen_ai.agent.id'] = correlation.agentId;
      if (correlation.agentName) params['gen_ai.agent.name'] = correlation.agentName;
      if (correlation.agentVersion) params['gen_ai.agent.version'] = correlation.agentVersion;
    }
    void this.#telemetry.track(correlation?.endpoint, {
      name: 'gen_ai.evaluation.result',
      properties: params,
      operationId: correlation?.traceId,
    });
  }
}
